import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ProfileSection } from './ProfileSection';
import { Avatar } from './Avatar';
import { useProfile } from '../../hooks/useProfile';
import { useState, useEffect, useRef } from 'react';
import { useToast } from '@shared/hooks/use-toast';
import { ConfirmPasswordModal, ConfirmPasswordModalRef } from '../modals/ConfirmPasswordModal';

export const Information = () => {
  const { t } = useTranslation('profileModule');
  const { data: profile, updateUser } = useProfile();
  const { toast } = useToast();
  // const queryClient = useQueryClient(); // Eliminar esta línea

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [originalEmail, setOriginalEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const confirmPasswordModalRef = useRef<ConfirmPasswordModalRef>(null);
  const pendingFormData = useRef<{
    name: string;
    email: string;
  } | null>(null);

  // Ref para el form
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    if (profile) {
      setName(profile.user.name || '');
      setEmail(profile.user.email || '');
      setOriginalEmail(profile.user.email || '');
    }
  }, [profile]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validación nativa
    if (formRef.current && !formRef.current.checkValidity()) {
      formRef.current.reportValidity();
      return;
    }

    // Determinar qué campos han cambiado
    const hasNameChanged = name.trim() !== (profile?.user.name || '');
    const hasEmailChanged = email !== originalEmail;

    // Si solo cambió el email, usar el flujo del modal
    if (!hasNameChanged && hasEmailChanged) {
      // Guardar datos pendientes y abrir modal de confirmación
      pendingFormData.current = { name, email };
      confirmPasswordModalRef.current?.open(email, async () => {
        setEmail(originalEmail);
        // No actualizar originalEmail ni hacer refetch, ya que el email real no cambia hasta la verificación
      });
      return;
    }

    // Si cambió el nombre pero NO el email, ejecutar saveProfile directamente
    if (hasNameChanged && !hasEmailChanged) {
      await saveProfile();
      return;
    }

    // Si cambió tanto el nombre como el email, abrir modal y ejecutar ambas operaciones después
    if (hasNameChanged && hasEmailChanged) {
      // Guardar datos pendientes y abrir modal de confirmación
      pendingFormData.current = { name, email };
      confirmPasswordModalRef.current?.open(email, async () => {
        // Después de confirmar la contraseña, ejecutar ambas operaciones
        try {
          // 1. Primero guardar nombre
          await saveProfile();

          // 2. Luego actualizar email (esto ya se hace en el modal)
          // El modal ya maneja updateEmail internamente

          setEmail(originalEmail);
        } catch (error) {
          // Si falla saveProfile, el email ya se actualizó en el modal
          // pero podemos mostrar un mensaje específico
          console.log('🔍 Information - Error en saveProfile después de confirmar:', error);
        }
      });
    }
  };

  const saveProfile = async () => {
    try {
      setIsSaving(true);
      await updateUser({
        name: name.trim(),
      });
      toast({
        title: t('information.success_title'),
        description: t('information.success_description'),
      });
    } catch (error: any) {
      // 🔍 Log para debugging - información estructurada para identificar problemas
      console.log('🔍 Information - Error:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
      });

      // 1. Mensaje por defecto (genérico) - fallback de seguridad
      let errorMessage = t('information.error_unknown');

      // 2. Si hay mensaje específico del servidor, analizarlo
      if (error?.response?.data?.message) {
        const serverMessage = error.response.data.message;

        // 3. Determinar tipo específico de error según el contenido del mensaje
        if (serverMessage.includes('profile_photo_path') || serverMessage.includes('too long')) {
          // Error de URL corrupta o datos muy largos - problema del servidor
          errorMessage = 'Error en el servidor: Problema con datos del perfil. Contacta al administrador.';
        } else if (serverMessage.includes('name') || serverMessage.includes('phone')) {
          // Error relacionado con nombre o teléfono
          errorMessage = 'Error al actualizar la información personal. Verifica los datos ingresados.';
        } else {
          // 4. Si no coincide con ningún patrón específico, truncar mensajes largos
          // para evitar mostrar URLs corruptas o errores técnicos muy extensos
          errorMessage = serverMessage.length > 200 ? serverMessage.substring(0, 200) + '...' : serverMessage;
        }
      } else if (error?.message) {
        // 5. Si no hay mensaje del servidor pero hay error.message (errores de red, etc.)
        errorMessage = error.message;
      }
      // 6. Si no hay nada, se mantiene el mensaje genérico inicial

      toast({
        title: t('information.error_title'),
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Elimina handleConfirmPassword, ya no es necesario

  return (
    <>
      <ProfileSection
        title={t('information.title')}
        description={t('information.description')}
        className={`transition-opacity duration-300 ${isSaving ? 'pointer-events-none opacity-90' : ''}`}
        data-testid="profile-information-section"
      >
        <form ref={formRef} onSubmit={handleSubmit}>
          <div className="flex flex-col gap-6 sm:flex-row sm:items-start">
            <div className="flex shrink-0 flex-col items-center sm:pt-1" data-testid="profile-avatar-section">
              <Avatar />
            </div>

            <div className="min-w-0 flex-1 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium mb-1">
                  {t('information.name_label')}
                </label>
                <Input
                  id="name"
                  name="name"
                  placeholder={t('information.name_placeholder')}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={isSaving}
                  maxLength={30}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('information.email_label')}</label>
                <Input value={email} onChange={(e) => setEmail(e.target.value)} disabled={isSaving} />
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <Button type="submit" className="w-full sm:w-auto" disabled={isSaving}>
              {isSaving ? t('information.saving') : t('information.submit')}
            </Button>
          </div>
        </form>
      </ProfileSection>
      <ConfirmPasswordModal ref={confirmPasswordModalRef} onCancel={() => setEmail(originalEmail)} />
    </>
  );
};
